/**
 * Time filtering for enriched breakdowns based on TimeFilterPreset
 * Uses actual date comparisons instead of string matching
 */

import { TimeFilterPreset } from '@/types/rebateAnalytics';
import { EnrichedBreakdown } from './types';

/**
 * Calculate the reference date for time filtering based on the data year.
 * For past years (dataYear < actualCurrentYear), the reference date is December 31 of that year.
 * For current year, the reference date is the actual current date.
 *
 * @param dataYear - The year of the data being viewed (optional)
 * @returns Reference date to use for "current" calculations
 */
export function getReferenceDate(dataYear?: number): Date {
  const now = new Date();
  const actualCurrentYear = now.getUTCFullYear();

  if (dataYear !== undefined && dataYear < actualCurrentYear) {
    // For past years, "current" means December
    return new Date(Date.UTC(dataYear, 11, 31, 0, 0, 0, 0));
  }

  // For current year (or undefined), use actual current date
  return now;
}

/**
 * Filter enriched breakdowns by time preset
 *
 * @param enrichedBreakdowns - Array of enriched breakdowns to filter
 * @param preset - Time filter preset to apply
 * @param currentDate - Current date for relative filtering (defaults to now in UTC)
 * @param ignoreYear - Whether to ignore year in comparisons (for YOY)
 * @param dataYear - The year of the data being viewed (for past year handling)
 * @returns Filtered array of enriched breakdowns
 */
export function filterByTimePreset(
  enrichedBreakdowns: EnrichedBreakdown[],
  preset: TimeFilterPreset,
  currentDate: Date = new Date(),
  ignoreYear: boolean = false,
  dataYear?: number,
): EnrichedBreakdown[] {
  // Use reference date based on data year for proper past year handling
  const referenceDate =
    dataYear !== undefined ? getReferenceDate(dataYear) : currentDate;

  // Normalize reference date to UTC midnight
  const normalizedCurrent = new Date(
    Date.UTC(
      referenceDate.getUTCFullYear(),
      referenceDate.getUTCMonth(),
      referenceDate.getUTCDate(),
      0,
      0,
      0,
      0,
    ),
  );

  switch (preset) {
    case 'all':
      // Show all data points
      return enrichedBreakdowns;

    case 'ytd': {
      // Year to date: January through current month of current year
      const currentYear = normalizedCurrent.getUTCFullYear();
      const currentMonth = normalizedCurrent.getUTCMonth();

      return enrichedBreakdowns.filter((breakdown) => {
        const breakdownYear = breakdown.date.getUTCFullYear();
        const breakdownMonth = breakdown.date.getUTCMonth();

        return breakdownYear === currentYear && breakdownMonth <= currentMonth;
      });
    }

    case 'yoy':
      // Year over year: show all months (YOY comparison happens in enhancement step)
      return enrichedBreakdowns;

    case 'current': {
      // Current month only
      const currentYear = normalizedCurrent.getUTCFullYear();
      const currentMonth = normalizedCurrent.getUTCMonth();

      return enrichedBreakdowns.filter((breakdown) => {
        if (ignoreYear) {
          // For YOY comparisons, match only by month
          return breakdown.date.getUTCMonth() === currentMonth;
        }
        return (
          breakdown.date.getUTCFullYear() === currentYear &&
          breakdown.date.getUTCMonth() === currentMonth
        );
      });
    }

    case 'previous': {
      // Previous month only
      let prevMonth = normalizedCurrent.getUTCMonth() - 1;
      let prevYear = normalizedCurrent.getUTCFullYear();

      if (prevMonth < 0) {
        prevMonth = 11;
        prevYear -= 1;
      }

      return enrichedBreakdowns.filter((breakdown) => {
        if (ignoreYear) {
          // For YOY comparisons, match only by month
          return breakdown.date.getUTCMonth() === prevMonth;
        }
        return (
          breakdown.date.getUTCFullYear() === prevYear &&
          breakdown.date.getUTCMonth() === prevMonth
        );
      });
    }

    case 'prev_and_current': {
      // Previous month + current month
      const currentYear = normalizedCurrent.getUTCFullYear();
      const currentMonth = normalizedCurrent.getUTCMonth();

      let prevMonth = currentMonth - 1;
      let prevYear = currentYear;

      if (prevMonth < 0) {
        prevMonth = 11;
        prevYear -= 1;
      }

      return enrichedBreakdowns.filter((breakdown) => {
        const year = breakdown.date.getUTCFullYear();
        const month = breakdown.date.getUTCMonth();

        if (ignoreYear) {
          // For YOY comparisons, match only by month
          return month === currentMonth || month === prevMonth;
        }
        return (
          (year === currentYear && month === currentMonth) ||
          (year === prevYear && month === prevMonth)
        );
      });
    }

    case 'current_and_next': {
      // Current month + next month
      const currentYear = normalizedCurrent.getUTCFullYear();
      const currentMonth = normalizedCurrent.getUTCMonth();

      let nextMonth = currentMonth + 1;
      let nextYear = currentYear;

      if (nextMonth > 11) {
        nextMonth = 0;
        nextYear += 1;
      }

      return enrichedBreakdowns.filter((breakdown) => {
        const year = breakdown.date.getUTCFullYear();
        const month = breakdown.date.getUTCMonth();

        if (ignoreYear) {
          // For YOY comparisons, match only by month
          return month === currentMonth || month === nextMonth;
        }
        return (
          (year === currentYear && month === currentMonth) ||
          (year === nextYear && month === nextMonth)
        );
      });
    }

    default:
      // Unknown preset - return all data
      return enrichedBreakdowns;
  }
}
